/**
 * Telegraphs — the ground decal an ability draws during its wind-up.
 *
 * This is not decoration. GAME-DESIGN puts every ability's `windupMs` in the
 * roster table specifically so opponents can react, and a wind-up nobody can see
 * is a wind-up that does not exist. Monolith's Fault Line has a 600ms wind-up
 * and 55 damage plus a 2s stun; that trade is only fair if the 14-metre line is
 * on the floor for those 600ms.
 *
 * The shape is read straight from `HitShape` in `@chaos/protocol` — the SAME
 * definition the server resolves the hit against. A telegraph that is drawn from
 * its own numbers will eventually disagree with the hitbox, and the player who
 * dodged correctly and died anyway will be right to be angry.
 *
 * The decal fills from 0 to 1 over the wind-up, so "how long do I have" is
 * readable at a glance rather than requiring a memorised table.
 *
 * ── Pass 3: drawn, pooled, prewarmed ──────────────────────────────────────
 *
 * Pass 1's decals were additive (they vanished on a bright deck and read as
 * thin hoops where they showed) and each cast built a NEW node material with
 * the colour baked in as a constant — so the first cast of every character ×
 * shape compiled a shader mid-fight, and every cast after that still paid a
 * material build and a geometry upload. Now each shape is a pooled CelShape:
 * alpha-blended cel paint in the floor-safe colour with an ink rim, a low
 * hazard fill and a HOT sweep edge as the clock; unit geometry scaled to the
 * ability's numbers; colour, progress, half-angle and fade as uniforms. Five
 * programs for the whole roster, compiled by the prewarm.
 */

import * as THREE from 'three/webgpu';
import { Fn, uv, float, smoothstep, step, uniform, mix, atan, fract } from 'three/tsl';
import { CHARACTERS, abilityAnchor } from '@chaos/protocol';
import {
  forwardFromYaw,
  GROUND_ZONE_OFFSET,
  PROJECTILE_LAUNCH_SIN,
  PROJECTILE_LAUNCH_COS,
  PROJECTILE_SPAWN_OFFSET,
  PLAYER_HALF_HEIGHT,
  DT,
} from '@chaos/sim';
import { telegraphHex } from '@chaos/protocol';
import { session, localPlayer, remotePlayers, roster } from '../state.js';
import { DECAL_LIFT } from './decals.js';
import { acquireShape, commonUniforms, ramp, band, baseMaterial, circleGeo, planeGeo, releaseCelShape } from '../vfx/CelShapes.js';

/** Ground height under a caster; the sim's floor plane for the graybox maps. */
let _floorY = 0;

let _scene = null;
const _active = [];
/** Optional caster-position resolver: (casterId, outVec3) => boolean. The
 *  character mockup injects one so body-anchored decals follow ITS bodies;
 *  the game falls back to state.js. Presentation plumbing only — the decal
 *  GEOMETRY still comes from the roster and nowhere else. */
let _casterResolver = null;

export function initTelegraphs(scene, floorY = 0, casterResolver = null) {
  _scene = scene;
  _floorY = floorY;
  _casterResolver = casterResolver;
}

export function setTelegraphFloor(y) {
  _floorY = y;
}

// ─── The shapes ─────────────────────────────────────────────────
//
// Each returns { mesh, u } with u.progress (the clock), u.fade, u.tint. The
// area is a low fill (the danger area is the whole shape from frame one),
// the sweep is a brighter fill with a HOT leading edge, and the rim is ink
// with a keyline. Unit geometry; the caller scales it.

/** The common danger fill + sweep for a radial shape on `r` (0 centre → 1 edge). */
function radialSweep(c, u, r, inside) {
  const swept = step(r, u.progress).mul(inside);
  const lead = band(r.sub(u.progress), -0.05, 0.0, 0.015).mul(inside);
  return { swept, lead };
}

function createDisc(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const c = ramp(u);
  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const inside = smoothstep(float(1.0), float(0.985), r);
  const rim = band(r, 0.955, 1.0, 0.012);
  const key = band(r, 0.925, 0.95, 0.01);
  const { swept, lead } = radialSweep(c, { progress }, r, inside);
  mat.colorNode = Fn(() => {
    let col = c.body;
    col = mix(col, c.hot, swept.mul(0.5));
    col = mix(col, c.core, lead);
    col = mix(col, c.core, key);
    col = mix(col, c.ink, rim);
    return col;
  })();
  mat.opacityNode = Fn(() => inside.mul(0.16).add(swept.mul(0.16)).add(lead.mul(0.8)).add(key.mul(0.55)).add(rim.mul(0.9)).min(1).mul(u.fade))();
  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress } };
}

function createCone(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const half = uniform(0.5);
  const c = ramp(u);
  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  // atan(x, y): 0 = the disc's +Y = the caster's forward (the mesh is yawed by the caller).
  const ang = atan(local.x, local.y).abs();
  const inCone = step(ang, half);
  const inside = inCone.mul(smoothstep(float(1.0), float(0.985), r));
  const rim = band(r, 0.955, 1.0, 0.012).mul(inCone);
  const key = band(r, 0.925, 0.95, 0.01).mul(inCone);
  // The straight edges: within a sliver of the sector's half-angle, in arc units.
  const edgeD = half.sub(ang).mul(r);
  const sideInk = smoothstep(float(0.03), float(0.012), edgeD).mul(inCone).mul(step(float(0.06), r));
  const sideKey = smoothstep(float(0.05), float(0.032), edgeD).mul(step(float(0.03), edgeD)).mul(inCone).mul(step(float(0.06), r));
  const { swept, lead } = radialSweep(c, { progress }, r, inside);
  mat.colorNode = Fn(() => {
    let col = c.body;
    col = mix(col, c.hot, swept.mul(0.5));
    col = mix(col, c.core, lead);
    col = mix(col, c.core, key.max(sideKey));
    col = mix(col, c.ink, rim.max(sideInk));
    return col;
  })();
  mat.opacityNode = Fn(() => inside.mul(0.16).add(swept.mul(0.16)).add(lead.mul(0.8)).add(key.max(sideKey).mul(0.55)).add(rim.max(sideInk).mul(0.9)).min(1).mul(u.fade))();
  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress, half } };
}

function createLine(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const c = ramp(u);
  const along = uv().y;
  const across = uv().x.sub(0.5).abs().mul(2);
  const rails = smoothstep(float(0.9), float(0.93), across);
  const key = band(across, 0.82, 0.88, 0.02);
  const ends = smoothstep(float(0.02), float(0.0), along).max(smoothstep(float(0.98), float(1.0), along));
  const swept = step(along, progress);
  const lead = band(along.sub(progress), -0.03, 0.0, 0.01);
  mat.colorNode = Fn(() => {
    let col = c.body;
    col = mix(col, c.hot, swept.mul(0.5));
    col = mix(col, c.core, lead);
    col = mix(col, c.core, key);
    col = mix(col, c.ink, rails.max(ends));
    return col;
  })();
  mat.opacityNode = Fn(() => float(0.16).add(swept.mul(0.16)).add(lead.mul(0.8)).add(key.mul(0.55)).add(rails.max(ends).mul(0.9)).min(1).mul(u.fade))();
  const mesh = new THREE.Mesh(planeGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress } };
}

/**
 * A rim at the acquisition radius of a nearest-target ability. Deliberately
 * NOT a filled disc: Chain Lightning and Displace strike ONE target — whoever is
 * nearest — and a filled circle reads as "everyone inside is hit", which is a lie
 * in both directions. The band brightens over the wind-up.
 */
function createRim(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const c = ramp(u);
  const r = uv().sub(0.5).mul(2).length();
  const bandM = band(r, 0.93, 1.0, 0.012);
  const key = band(r, 0.955, 0.975, 0.008);
  mat.colorNode = Fn(() => {
    let col = mix(c.body, c.hot, progress.mul(0.6));
    col = mix(col, c.core, key);
    col = mix(col, c.ink, band(r, 0.985, 1.0, 0.008).max(band(r, 0.93, 0.945, 0.008)));
    return col;
  })();
  mat.opacityNode = Fn(() => bandM.mul(float(0.55).add(progress.mul(0.4))).mul(u.fade))();
  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress } };
}

/**
 * The lock-on marker for nearest-target abilities, drawn at the feet of whoever
 * the sim would pick RIGHT NOW. The ring tightens over the wind-up — if it is
 * closing on you, you are the target and the fix is to stop being nearest.
 */
function createReticle(colorHex) {
  const mat = baseMaterial();
  const u = commonUniforms(colorHex);
  const progress = uniform(0);
  const c = ramp(u);
  const local = uv().sub(0.5).mul(2);
  const r = local.length();
  const outer = mix(float(1.0), float(0.62), progress);
  const inner = outer.sub(0.22);
  const ringM = smoothstep(outer, outer.sub(0.03), r).mul(smoothstep(inner, inner.add(0.03), r));
  const key = smoothstep(outer.sub(0.09), outer.sub(0.11), r).mul(smoothstep(outer.sub(0.14), outer.sub(0.12), r));
  const ink = smoothstep(outer.sub(0.03), outer, r).max(smoothstep(inner.add(0.03), inner, r));
  // Four ticks at the compass points, so it reads as a SIGHT and not a hoop.
  const ang = atan(local.x, local.y);
  const ticks = step(float(0.9), fract(ang.mul(4 / (Math.PI * 2)).add(0.05)).sub(0.5).abs().mul(2).oneMinus());
  mat.colorNode = Fn(() => {
    let col = c.body;
    col = mix(col, c.core, key.max(ticks.mul(ringM)));
    col = mix(col, c.ink, ink.mul(ringM));
    return col;
  })();
  mat.opacityNode = Fn(() => ringM.mul(0.9).mul(u.fade))();
  const mesh = new THREE.Mesh(circleGeo(), mat);
  mesh.userData.sharedGeo = true;
  return { mesh, u: { ...u, progress } };
}

export function teleDisc(colorHex) {
  return acquireShape('teleDisc', () => createDisc(colorHex), colorHex, { floor: true });
}
export function teleCone(colorHex, halfRad) {
  const r = acquireShape('teleCone', () => createCone(colorHex), colorHex, { floor: true });
  r.u.half.value = halfRad;
  return r;
}
export function teleLine(colorHex) {
  return acquireShape('teleLine', () => createLine(colorHex), colorHex, { floor: true });
}
export function teleRim(colorHex) {
  return acquireShape('teleRim', () => createRim(colorHex), colorHex, { floor: true });
}
export function teleReticle(colorHex) {
  return acquireShape('teleReticle', () => createReticle(colorHex), colorHex, { floor: true });
}
/** Every pooled key this module can produce, for the prewarm and its test. */
export const TELEGRAPH_SHAPE_KEYS = ['teleDisc', 'teleCone', 'teleLine', 'teleRim', 'teleReticle'];

/**
 * Flat-ground landing distance of a lobbed projectile, integrated with the
 * sim's own tick math (Euler, gravity-then-move, range spent after moving) so
 * the drawn ring and the real impact agree to the tick. Assumes the ground at
 * the landing point is the caster's own floor — on uneven terrain the shell
 * lands short (a wall) or long (a drop), which the client cannot know during
 * the wind-up; the flat answer is right on every open lane, which is where
 * Arc Shot is actually thrown.
 */
export function projectileLandingDistance(shape) {
  let vy = shape.speed * PROJECTILE_LAUNCH_SIN;
  const vh = shape.speed * PROJECTILE_LAUNCH_COS;
  // Muzzle: caster centre + 0.4 (spawnProjectile), centre = feet + PLAYER_HALF_HEIGHT.
  let relY = PLAYER_HALF_HEIGHT + 0.4;
  let dist = PROJECTILE_SPAWN_OFFSET;
  let range = shape.maxRange;
  for (let i = 0; i < 300 && relY > 0 && range > 0; i++) {
    vy -= shape.gravity * DT;
    relY += vy * DT;
    const step = vh * DT;
    dist += step;
    range -= step;
  }
  return dist;
}

/** Matches the sim's `nearestTarget`: nearest live enemy of the caster, 3D. */
function findNearestTarget(originX, originCenterY, originZ, radius, out) {
  let bestD2 = radius * radius;
  let found = false;
  const consider = (x, y, z) => {
    // Anything standing on the cast origin is the caster; players separate to
    // 1.0u so a real target cannot occupy this disc.
    const cx = x - originX;
    const cz = z - originZ;
    if (cx * cx + cz * cz < 0.81) return;
    const dy = y - originCenterY;
    const d2 = cx * cx + cz * cz + dy * dy;
    if (d2 >= bestD2) return;
    bestD2 = d2;
    out.set(x, y, z);
    found = true;
  };

  if (localPlayer.phase === 'alive') {
    consider(localPlayer.renderPos.x, localPlayer.renderPos.y, localPlayer.renderPos.z);
  }
  for (const [id, mesh] of remotePlayers) {
    const phase = roster.get(id)?.phase;
    if (phase && phase !== 'alive') continue;
    consider(mesh.position.x, mesh.position.y, mesh.position.z);
  }
  return found;
}

/**
 * Builds the telegraph for an ability and starts its clock.
 *
 * `playerId` is the caster. abilityAnchor() decides what the decal is a promise
 * about: a WORLD ability (Fault Line, the ground zones, Arc Shot's landing
 * ring) resolves from the frozen cast origin, so its decal is nailed to the
 * floor where it was drawn. A BODY ability (melee cones, the caster-centred
 * spheres, Blink Strike's own dash) resolves from the caster's LIVE position at
 * the resolve tick, so its decal must track the caster — a cone frozen where
 * the swing STARTED marks ground the swing will never touch.
 *
 * Returns null for abilities that do not project onto the ground (`self`), which
 * is correct — Bulwark and Overload change the caster, and their tell is the
 * caster's own VFX, not a floor decal.
 */
export function showTelegraph({ abilityId, characterId, playerId, x, y, z, yaw }) {
  const ability = findAbility(characterId, abilityId);
  if (!ability) return null;
  const shape = ability.hit;
  if (shape.kind === 'self') return null;

  // Zero wind-up means nothing to react to, so drawing a decal would be a lie —
  // it implies a window that does not exist.
  const windupMs = ability.windupMs;
  if (windupMs <= 0) return null;

  // The FLOOR-SAFE tint, not the character's identity colour: the deck already
  // spends red on hazard and green on mend. See the note in characters.ts.
  const colorHex = telegraphHex(characterId);

  // A bullet is its own tell: it is on screen from the muzzle to the impact
  // (combat/Projectiles.js), and a decal for a 60-130ms wind-up under a
  // shooter would only say "something is about to leave here", which the
  // muzzle already says. The SHOOTER's own reach is the aim indicator.
  if (shape.kind === 'shot') return null;

  let built = null;
  let forwardOffset = 0;
  let nearestRadius = 0;
  switch (shape.kind) {
    case 'melee_cone':
      built = teleCone(colorHex, (shape.halfAngleDeg * Math.PI) / 180);
      built.mesh.scale.setScalar(shape.range);
      break;
    case 'sphere': {
      // Chain Lightning and Displace pick the NEAREST target inside the radius;
      // a filled AoE disc would promise area damage that does not exist. They
      // get a rim (acquisition range) plus a tracking reticle on the current pick.
      const nearestOnly = (ability.effects ?? []).some(
        (e) => e.kind === 'chain' || e.kind === 'swap_position'
      );
      built = nearestOnly ? teleRim(colorHex) : teleDisc(colorHex);
      built.mesh.scale.setScalar(shape.radius);
      if (nearestOnly) nearestRadius = shape.radius;
      break;
    }
    case 'ground_zone':
      // Draw the authored radius, full stop. stepBarrage clamps shell CENTRES
      // to `radius - BARRAGE_SHELL_SPLASH`, so no blast escapes this circle and
      // the drawn edge is the true threat edge. The sim places Barrage 14m
      // downrange (GROUND_ZONE_OFFSET); a decal without that offset warns the
      // caster's feet and leaves the real target area unmarked.
      built = teleDisc(colorHex);
      built.mesh.scale.setScalar(shape.radius);
      forwardOffset = GROUND_ZONE_OFFSET[abilityId] ?? 0;
      break;
    case 'dash_line': {
      // Mirror the sim's resolve exactly: Blink Strike's segment is its
      // selfMove sweep. The endpoint test is a rounded RECTANGLE
      // (pointRectDist2XZ ≤ PLAYER_RADIUS), so the strike reaches exactly the
      // drawn tip plus the 0.5 body pad — the same implicit pad every other
      // decal speaks. Draw the authored length, not length+width/2.
      const lineLen = ability.selfMove && ability.selfMove > 0 ? ability.selfMove : shape.length;
      built = teleLine(colorHex);
      built.mesh.scale.set(shape.width, lineLen, 1);
      forwardOffset = lineLen / 2;
      break;
    }
    case 'projectile':
      // Arc Shot is lobbed: on flat ground it can ONLY connect at the ballistic
      // landing ring, so that ring is the telegraph. An aim line would paint the
      // ~26m under the arc where the shell physically cannot hit.
      built = teleDisc(colorHex);
      built.mesh.scale.setScalar(shape.radius);
      forwardOffset = projectileLandingDistance(shape);
      break;
    default:
      return null;
  }

  const { mesh, u } = built;
  mesh.name = `telegraph:${abilityId}`;
  const fwd = forwardFromYaw(yaw);
  mesh.position.set(
    x + fwd.x * forwardOffset,
    (y ?? _floorY) + DECAL_LIFT,
    z + fwd.z * forwardOffset
  );
  // Laid flat (rotation.x = -PI/2, Euler XYZ); rotation.z = yaw maps the
  // plane's local +Y onto forwardFromYaw(yaw) — verified numerically.
  mesh.rotation.x = -Math.PI / 2;
  mesh.rotation.z = yaw;
  mesh.renderOrder = 4;
  mesh.frustumCulled = false;
  _scene.add(mesh);

  const entry = { mesh, progress: u.progress, fade: u.fade, born: performance.now(), windupMs, abilityId };
  // Body-anchored decals re-position from the caster's rendered transform every
  // frame (frozen yaw, live feet) until resolve. Yaw stays captured at commit —
  // both anchor kinds freeze aim — so only the offset origin moves.
  if (abilityAnchor(ability) === 'body') {
    // `guard`: a selfMove decal (Blink Strike) must stop following at the dash
    // itself — the sim sweeps from the pre-dash body, and the local prediction
    // applies the 9m jump BEFORE the wind-up clock fires, so following it slid
    // the drawn line ~6m past the one actually swept. Other body decals keep
    // following through teleports (a swapped caster's slam moves with them).
    entry.follow = {
      casterId: playerId,
      fwdX: fwd.x,
      fwdZ: fwd.z,
      forwardOffset,
      guard: (ability.selfMove ?? 0) > 0,
      px: x,
      pz: z,
    };
  }
  _active.push(entry);

  if (nearestRadius > 0) {
    const ret = teleReticle(colorHex);
    ret.mesh.name = `telegraph-reticle:${abilityId}`;
    ret.mesh.scale.setScalar(1.1);
    ret.mesh.rotation.x = -Math.PI / 2;
    ret.mesh.renderOrder = 5;
    ret.mesh.frustumCulled = false;
    _scene.add(ret.mesh);
    const track = {
      mesh: ret.mesh,
      progress: ret.u.progress,
      fade: ret.u.fade,
      born: entry.born,
      windupMs,
      abilityId,
      nearest: {
        x,
        z,
        floorY: (y ?? _floorY) + DECAL_LIFT + 0.02,
        centerY: (y ?? _floorY) + PLAYER_HALF_HEIGHT,
        radius: nearestRadius,
      },
      dimmed: false,
    };
    // Chain Lightning and Displace are body-anchored: the sim acquires from the
    // caster's live position at resolve, so the acquisition CENTRE the reticle
    // searches from must follow the caster, not the cast-time origin.
    if (entry.follow) track.follow = { casterId: playerId };
    positionReticle(track);
    _active.push(track);
  }

  return entry;
}

const _nearestScratch = new THREE.Vector3();
const _followScratch = new THREE.Vector3();

/** The caster's rendered transform — the same source the character meshes draw
 *  from, so a following decal can never be somewhere the body visibly is not.
 *  Remote casters are interpolated ~100ms behind the sim; their MESH is the
 *  honest position on this screen. Returns null if the caster has no body. */
function casterRenderPos(casterId, out) {
  if (_casterResolver) return _casterResolver(casterId, out) ? out : null;
  if (casterId === session.userId) return out.copy(localPlayer.renderPos);
  const mesh = remotePlayers.get(casterId);
  return mesh ? out.copy(mesh.position) : null;
}

/** No walk speed covers this in one frame; the predicted dash jump does. m². */
const TELEPORT_JUMP2 = 2.5 * 2.5;

/** Re-seats a body-anchored decal under the caster's current feet, frozen yaw
 *  intact. A vanished caster keeps the last drawn spot. */
function positionFollow(t) {
  const f = t.follow;
  if (!casterRenderPos(f.casterId, _followScratch)) return;
  if (f.guard) {
    const dx = _followScratch.x - f.px;
    const dz = _followScratch.z - f.pz;
    // The dash landed (prediction runs ahead of the wind-up clock): the sweep
    // ran from the previous seat, so stop following — permanently.
    if (dx * dx + dz * dz > TELEPORT_JUMP2) {
      t.follow = null;
      return;
    }
    f.px = _followScratch.x;
    f.pz = _followScratch.z;
  }
  t.mesh.position.set(
    _followScratch.x + f.fwdX * f.forwardOffset,
    _followScratch.y - PLAYER_HALF_HEIGHT + DECAL_LIFT,
    _followScratch.z + f.fwdZ * f.forwardOffset
  );
}

/** Moves a tracking reticle's acquisition centre onto the caster's live body —
 *  the sim measures "nearest" from wherever the caster IS at resolve. */
function refreshNearestOrigin(t) {
  if (!casterRenderPos(t.follow.casterId, _followScratch)) return;
  const n = t.nearest;
  n.x = _followScratch.x;
  n.z = _followScratch.z;
  n.centerY = _followScratch.y; // renderPos/mesh y IS the capsule centre
  n.floorY = _followScratch.y - PLAYER_HALF_HEIGHT + DECAL_LIFT + 0.02;
}

/**
 * Re-aims a tracking reticle at whoever is nearest RIGHT NOW — the sim decides
 * at resolve time, not at cast time, so a static marker would go stale the
 * moment anyone moved. With nobody in range the reticle collapses onto the
 * caster, dim: "cast now and it hits nothing".
 */
function positionReticle(t) {
  const n = t.nearest;
  if (findNearestTarget(n.x, n.centerY, n.z, n.radius, _nearestScratch)) {
    t.mesh.position.set(
      _nearestScratch.x,
      _nearestScratch.y - PLAYER_HALF_HEIGHT + DECAL_LIFT + 0.02,
      _nearestScratch.z
    );
    t.dimmed = false;
  } else {
    t.mesh.position.set(n.x, n.floorY, n.z);
    t.dimmed = true;
  }
}

export function updateTelegraphs() {
  const now = performance.now();
  for (let i = _active.length - 1; i >= 0; i--) {
    const t = _active[i];
    const elapsed = now - t.born;
    const p = elapsed / t.windupMs;

    if (p >= 1) {
      // A 120ms afterglow past the resolve so the hit and its warning overlap by
      // one beat instead of the decal vanishing a frame before the damage.
      // Tracking reticles freeze here: the sim picked its target at resolve, so
      // the last pre-resolve aim is the honest one.
      const after = (elapsed - t.windupMs) / 120;
      if (after >= 1) {
        releaseCelShape(t.mesh);
        _active.splice(i, 1);
        continue;
      }
      // Body-anchored decals freeze here too: the shape resolved from the
      // caster's position AT the resolve tick, so the last pre-resolve seat is
      // where the hit actually happened — sliding on through the afterglow
      // would drag the warning off the wound.
      t.progress.value = 1;
      t.fade.value = (1 - after) * (t.dimmed ? 0.35 : 1);
      continue;
    }

    // Body-anchored: re-seat under the caster's live body every wind-up frame,
    // mirroring the sim resolving from the live position at the resolve tick.
    if (t.follow) {
      if (t.nearest) refreshNearestOrigin(t);
      else positionFollow(t);
    }

    // Re-aim every frame during the wind-up — the sim picks nearest at RESOLVE
    // time, so a reticle placed once at cast goes stale the moment anyone moves.
    if (t.nearest) positionReticle(t);

    t.progress.value = p;
    t.fade.value = t.dimmed ? 0.35 : 1;
  }
}

/** For tests: the live decals. */
export function activeTelegraphs() {
  return _active.map((t) => ({ name: t.mesh.name, abilityId: t.abilityId, progress: t.progress.value }));
}

export function clearTelegraphs() {
  for (const t of _active) releaseCelShape(t.mesh);
  _active.length = 0;
}

function findAbility(characterId, abilityId) {
  const def = CHARACTERS[characterId];
  if (!def) return null;
  for (const slot of ['basic', 'skill1', 'skill2', 'ultimate']) {
    if (def.abilities[slot].id === abilityId) return def.abilities[slot];
  }
  return null;
}
